import * as THREE from 'three';

/**
 * Minimal merged-geometry builder. Everything static in the city is appended into a handful of
 * builders (one per material per chunk) and turned into a single BufferGeometry each — that is how
 * the full district stays well under the draw-call budget.
 *
 * Colours are float RGB (may exceed 1 for emissive "glow" materials feeding the bloom pass).
 */
export type UV4 = readonly [number, number, number, number];
export type RGB = readonly [number, number, number];

const _v = new THREE.Vector3();
const _n = new THREE.Vector3();
const _m3 = new THREE.Matrix3();

type Arrays = { pos: number[]; nor: number[]; uv: number[]; col: number[]; idx: number[] };

export class MeshBuilder {
  private a: Arrays = { pos: [], nor: [], uv: [], col: [], idx: [] };
  /**
   * Arrays appended after this builder's own, not yet copied in: build() writes them straight into the typed
   * arrays (the city's tier merges used to copy every chunk's arrays into another builder at boot). Reading or
   * writing this builder's arrays copies them in first, so it behaves exactly as if they had been appended.
   */
  private tail: Arrays[] = [];
  /** This builder's arrays are also another builder's tail: copied before they change (copy on write). */
  private shared = false;

  private own(): Arrays {
    if (this.shared) {
      const a = this.a;
      this.a = { pos: a.pos.slice(), nor: a.nor.slice(), uv: a.uv.slice(), col: a.col.slice(), idx: a.idx.slice() };
      this.shared = false;
    }
    if (this.tail.length) {
      const a = this.a;
      for (const o of this.tail) {
        const base = a.pos.length / 3;
        for (const v of o.pos) a.pos.push(v);
        for (const v of o.nor) a.nor.push(v);
        for (const v of o.uv) a.uv.push(v);
        for (const v of o.col) a.col.push(v);
        for (const i of o.idx) a.idx.push(i + base);
      }
      this.tail = [];
    }
    return this.a;
  }

  get pos(): number[] {
    return this.own().pos;
  }
  get nor(): number[] {
    return this.own().nor;
  }
  get uv(): number[] {
    return this.own().uv;
  }
  get col(): number[] {
    return this.own().col;
  }
  get idx(): number[] {
    return this.own().idx;
  }

  /** Append another builder's vertices and triangles after this one's (index counts add up). */
  append(o: MeshBuilder): void {
    const arrays = o.tail.length ? o.own() : o.a;
    if (arrays.idx.length === 0 && arrays.pos.length === 0) return;
    o.shared = true;
    // a builder appended to itself would grow while it is copied
    this.tail.push(o === this ? { pos: arrays.pos.slice(), nor: arrays.nor.slice(), uv: arrays.uv.slice(), col: arrays.col.slice(), idx: arrays.idx.slice() } : arrays);
  }

  get vertexCount(): number {
    let n = this.a.pos.length;
    for (const o of this.tail) n += o.pos.length;
    return n / 3;
  }

  /** Index count, appended builders included (without copying them in). */
  get indexCount(): number {
    let n = this.a.idx.length;
    for (const o of this.tail) n += o.idx.length;
    return n;
  }

  private vert(x: number, y: number, z: number, nx: number, ny: number, nz: number, u: number, v: number, c: RGB): number {
    const a = this.shared || this.tail.length ? this.own() : this.a;
    a.pos.push(x, y, z);
    a.nor.push(nx, ny, nz);
    a.uv.push(u, v);
    a.col.push(c[0], c[1], c[2]);
    return a.pos.length / 3 - 1;
  }

  /**
   * Quad from 4 corners (counter-clockwise when seen from the front): bottom-left, bottom-right,
   * top-right, top-left. UV rect maps u0..u1 left->right, v0..v1 bottom->top.
   */
  quad(p0: ArrayLike<number>, p1: ArrayLike<number>, p2: ArrayLike<number>, p3: ArrayLike<number>, uv: UV4, c: RGB, c2?: RGB): void {
    const ax = p1[0]! - p0[0]!;
    const ay = p1[1]! - p0[1]!;
    const az = p1[2]! - p0[2]!;
    const bx = p3[0]! - p0[0]!;
    const by = p3[1]! - p0[1]!;
    const bz = p3[2]! - p0[2]!;
    let nx = ay * bz - az * by;
    let ny = az * bx - ax * bz;
    let nz = ax * by - ay * bx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    const top = c2 ?? c;
    const i0 = this.vert(p0[0]!, p0[1]!, p0[2]!, nx, ny, nz, uv[0], uv[1], c);
    this.vert(p1[0]!, p1[1]!, p1[2]!, nx, ny, nz, uv[2], uv[1], c);
    this.vert(p2[0]!, p2[1]!, p2[2]!, nx, ny, nz, uv[2], uv[3], top);
    this.vert(p3[0]!, p3[1]!, p3[2]!, nx, ny, nz, uv[0], uv[3], top);
    this.idx.push(i0, i0 + 1, i0 + 2, i0, i0 + 2, i0 + 3);
  }

  /** Quad whose winding is fixed up so its normal points along `dir` (roofs, gables). */
  quadFacing(p0: ArrayLike<number>, p1: ArrayLike<number>, p2: ArrayLike<number>, p3: ArrayLike<number>, uv: UV4, c: RGB, dir: ArrayLike<number>): void {
    const n = triNormal(p0, p1, p3);
    if (n[0] * dir[0]! + n[1] * dir[1]! + n[2] * dir[2]! >= 0) this.quad(p0, p1, p2, p3, uv, c);
    else this.quad(p1, p0, p3, p2, [uv[2], uv[1], uv[0], uv[3]], c);
  }

  triFacing(p0: ArrayLike<number>, p1: ArrayLike<number>, p2: ArrayLike<number>, uv: UV4, c: RGB, dir: ArrayLike<number>): void {
    const n = triNormal(p0, p1, p2);
    if (n[0] * dir[0]! + n[1] * dir[1]! + n[2] * dir[2]! >= 0) this.tri(p0, p1, p2, uv, c);
    else this.tri(p1, p0, p2, uv, c);
  }

  tri(p0: ArrayLike<number>, p1: ArrayLike<number>, p2: ArrayLike<number>, uv: UV4, c: RGB): void {
    const ax = p1[0]! - p0[0]!;
    const ay = p1[1]! - p0[1]!;
    const az = p1[2]! - p0[2]!;
    const bx = p2[0]! - p0[0]!;
    const by = p2[1]! - p0[1]!;
    const bz = p2[2]! - p0[2]!;
    let nx = ay * bz - az * by;
    let ny = az * bx - ax * bz;
    let nz = ax * by - ay * bx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l;
    ny /= l;
    nz /= l;
    const i0 = this.vert(p0[0]!, p0[1]!, p0[2]!, nx, ny, nz, uv[0], uv[1], c);
    this.vert(p1[0]!, p1[1]!, p1[2]!, nx, ny, nz, uv[2], uv[1], c);
    this.vert(p2[0]!, p2[1]!, p2[2]!, nx, ny, nz, (uv[0] + uv[2]) / 2, uv[3], c);
    this.idx.push(i0, i0 + 1, i0 + 2);
  }

  /** Flat horizontal polygon (triangulated with earcut) at height y, facing up. UVs = world xz * scale. */
  polygon(points: { x: number; z: number }[], y: number, c: RGB, uvScale = 1, holes: { x: number; z: number }[][] = []): void {
    const contour = points.map((p) => new THREE.Vector2(p.x, p.z));
    const holeV = holes.map((h) => h.map((p) => new THREE.Vector2(p.x, p.z)));
    const tris = THREE.ShapeUtils.triangulateShape(contour, holeV);
    const all = [...points, ...holes.flat()];
    const base = this.vertexCount;
    for (const p of all) this.vert(p.x, y, p.z, 0, 1, 0, p.x * uvScale, -p.z * uvScale, c);
    for (const t of tris) {
      // ensure upward winding
      const a = all[t[0]!]!;
      const b = all[t[1]!]!;
      const d = all[t[2]!]!;
      const cross = (b.x - a.x) * (d.z - a.z) - (b.z - a.z) * (d.x - a.x);
      if (cross < 0) this.idx.push(base + t[0]!, base + t[1]!, base + t[2]!);
      else this.idx.push(base + t[0]!, base + t[2]!, base + t[1]!);
    }
  }

  /**
   * Axis-aligned (in local space) box, rotated by `rotY` around its centre-bottom, placed at
   * (x, y, z) = centre of the bottom face. `faces`: skip bottom by default.
   */
  box(x: number, y: number, z: number, sx: number, sy: number, sz: number, rotY: number, c: RGB, uv: UV4, opts: { bottom?: boolean; top?: boolean; topColour?: RGB; uvTop?: UV4; baseShade?: number } = {}): void {
    const cs = Math.cos(rotY);
    const sn = Math.sin(rotY);
    const hx = sx / 2;
    const hz = sz / 2;
    // local -> world: lx along (cos, -sin), lz along (sin, cos)   (heading convention)
    const P = (lx: number, ly: number, lz: number): [number, number, number] => [x + lx * cs + lz * sn, y + ly, z - lx * sn + lz * cs];
    const b0 = P(-hx, 0, -hz);
    const b1 = P(hx, 0, -hz);
    const b2 = P(hx, 0, hz);
    const b3 = P(-hx, 0, hz);
    const t0 = P(-hx, sy, -hz);
    const t1 = P(hx, sy, -hz);
    const t2 = P(hx, sy, hz);
    const t3 = P(-hx, sy, hz);
    // sides (CCW from outside); `baseShade` bakes contact occlusion into the foot of the sides (a soft
    // vertical gradient in the vertex colours, no extra vertices)
    const k = opts.baseShade ?? 1;
    const cb: RGB = k === 1 ? c : [c[0] * k, c[1] * k, c[2] * k];
    this.quad(b3, b2, t2, t3, uv, cb, c); // +Z (front)
    this.quad(b1, b0, t0, t1, uv, cb, c); // -Z
    this.quad(b2, b1, t1, t2, uv, cb, c); // +X? (depends on rot) right/left
    this.quad(b0, b3, t3, t0, uv, cb, c);
    if (opts.top !== false) this.quad(t3, t2, t1, t0, opts.uvTop ?? uv, opts.topColour ?? c);
    if (opts.bottom) this.quad(b0, b1, b2, b3, uv, c);
  }

  /** Vertical prism (cylinder approximation) around (x, z) from y0 to y1. */
  cylinder(x: number, z: number, y0: number, y1: number, r0: number, r1: number, segs: number, c: RGB, uv: UV4, opts: { top?: boolean; topColour?: RGB; phase?: number; baseShade?: number } = {}): void {
    const ph = opts.phase ?? 0;
    const k = opts.baseShade ?? 1;
    const cb: RGB = k === 1 ? c : [c[0] * k, c[1] * k, c[2] * k];
    // slanted side normal for tapered shapes (cones, spires, tapering chimneys)
    const hgt = y1 - y0;
    const slope = r0 - r1;
    const nl = Math.hypot(hgt, slope) || 1;
    const nh = hgt / nl;
    const ny = slope / nl;
    for (let i = 0; i < segs; i++) {
      const a0 = ph + (i / segs) * Math.PI * 2;
      const a1 = ph + ((i + 1) / segs) * Math.PI * 2;
      const c0 = Math.cos(a0);
      const s0 = Math.sin(a0);
      const c1 = Math.cos(a1);
      const s1 = Math.sin(a1);
      const u0 = uv[0] + ((uv[2] - uv[0]) * i) / segs;
      const u1 = uv[0] + ((uv[2] - uv[0]) * (i + 1)) / segs;
      // smooth normals per vertex for round things
      const base = this.vertexCount;
      this.vert(x + c1 * r0, y0, z + s1 * r0, c1 * nh, ny, s1 * nh, u0, uv[1], cb);
      this.vert(x + c0 * r0, y0, z + s0 * r0, c0 * nh, ny, s0 * nh, u1, uv[1], cb);
      this.vert(x + c0 * r1, y1, z + s0 * r1, c0 * nh, ny, s0 * nh, u1, uv[3], c);
      this.vert(x + c1 * r1, y1, z + s1 * r1, c1 * nh, ny, s1 * nh, u0, uv[3], c);
      this.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    if (opts.top !== false && r1 > 0.001) {
      const tc = opts.topColour ?? c;
      const centre = this.vert(x, y1, z, 0, 1, 0, (uv[0] + uv[2]) / 2, (uv[1] + uv[3]) / 2, tc);
      const first = this.vertexCount;
      for (let i = 0; i <= segs; i++) {
        const a = ph + (i / segs) * Math.PI * 2;
        this.vert(x + Math.cos(a) * r1, y1, z + Math.sin(a) * r1, 0, 1, 0, uv[0], uv[1], tc);
      }
      for (let i = 0; i < segs; i++) this.idx.push(centre, first + i + 1, first + i);
    }
  }

  /** Thin square-section beam between two 3D points (lattice girders, braces, ropes). */
  beam(a: ArrayLike<number>, b: ArrayLike<number>, w: number, c: RGB, uv: UV4): void {
    const d = new THREE.Vector3(b[0]! - a[0]!, b[1]! - a[1]!, b[2]! - a[2]!);
    const len = d.length();
    if (len < 1e-4) return;
    d.divideScalar(len);
    const ref = Math.abs(d.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(d, ref).normalize().multiplyScalar(w / 2);
    const v = new THREE.Vector3().crossVectors(d, u).normalize().multiplyScalar(w / 2);
    const corner = (p: ArrayLike<number>, su: number, sv: number): [number, number, number] => [p[0]! + u.x * su + v.x * sv, p[1]! + u.y * su + v.y * sv, p[2]! + u.z * su + v.z * sv];
    const sides: [number, number, number, number][] = [
      [1, 1, -1, 1],
      [-1, 1, -1, -1],
      [-1, -1, 1, -1],
      [1, -1, 1, 1],
    ];
    for (const [su0, sv0, su1, sv1] of sides) {
      const mid = [(su0 + su1) / 2, (sv0 + sv1) / 2];
      const out = [u.x * mid[0]! + v.x * mid[1]!, u.y * mid[0]! + v.y * mid[1]!, u.z * mid[0]! + v.z * mid[1]!];
      this.quadFacing(corner(a, su0, sv0), corner(a, su1, sv1), corner(b, su1, sv1), corner(b, su0, sv0), uv, c, out);
    }
  }

  /** Append an arbitrary geometry transformed by `m`, recoloured, UVs squeezed into `uv`. */
  geometry(geo: THREE.BufferGeometry, m: THREE.Matrix4, c: RGB, uv: UV4): void {
    const p = geo.getAttribute('position');
    const n = geo.getAttribute('normal');
    const t = geo.getAttribute('uv');
    _m3.getNormalMatrix(m);
    const base = this.vertexCount;
    for (let i = 0; i < p.count; i++) {
      _v.fromBufferAttribute(p, i).applyMatrix4(m);
      _n.fromBufferAttribute(n, i).applyMatrix3(_m3).normalize();
      const u = t ? t.getX(i) : 0.5;
      const v = t ? t.getY(i) : 0.5;
      this.vert(_v.x, _v.y, _v.z, _n.x, _n.y, _n.z, uv[0] + (uv[2] - uv[0]) * u, uv[1] + (uv[3] - uv[1]) * v, c);
    }
    const index = geo.getIndex();
    if (index) for (let i = 0; i < index.count; i++) this.idx.push(base + index.getX(i));
    else for (let i = 0; i < p.count; i++) this.idx.push(base + i);
  }

  build(): THREE.BufferGeometry | null {
    const parts = [this.a, ...this.tail];
    let nv = 0;
    let ni = 0;
    for (const p of parts) {
      nv += p.pos.length / 3;
      ni += p.idx.length;
    }
    if (ni === 0) return null;
    // straight into typed arrays, appended parts included (no intermediate concatenation)
    const pos = new Float32Array(nv * 3);
    const nor = new Float32Array(nv * 3);
    const uv = new Float32Array(nv * 2);
    const col = new Float32Array(nv * 3);
    const idx = nv > 65535 ? new Uint32Array(ni) : new Uint16Array(ni);
    let v = 0;
    let i = 0;
    for (const p of parts) {
      pos.set(p.pos, v * 3);
      nor.set(p.nor, v * 3);
      uv.set(p.uv, v * 2);
      col.set(p.col, v * 3);
      const src = p.idx;
      for (let k = 0; k < src.length; k++) idx[i + k] = src[k]! + v;
      v += p.pos.length / 3;
      i += src.length;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    setBounds(g, pos);
    return g;
  }
}

/**
 * computeBoundingBox + computeBoundingSphere over the position array directly (the same arithmetic as three's,
 * without its per-vertex accessor calls: ~0.4 s of a phone's boot for the city's chunks).
 */
function setBounds(g: THREE.BufferGeometry, pos: Float32Array): void {
  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;
  for (let k = 0; k < pos.length; k += 3) {
    const x = pos[k]!;
    const y = pos[k + 1]!;
    const z = pos[k + 2]!;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  const box = new THREE.Box3(new THREE.Vector3(minX, minY, minZ), new THREE.Vector3(maxX, maxY, maxZ));
  const center = box.getCenter(new THREE.Vector3());
  let r2 = 0;
  for (let k = 0; k < pos.length; k += 3) {
    const dx = center.x - pos[k]!;
    const dy = center.y - pos[k + 1]!;
    const dz = center.z - pos[k + 2]!;
    const d = dx * dx + dy * dy + dz * dz;
    if (d > r2) r2 = d;
  }
  g.boundingBox = box;
  g.boundingSphere = new THREE.Sphere(center, Math.sqrt(r2));
}

/** Builders keyed by (material key, chunk). */
export class ChunkedBuilders {
  private map = new Map<string, MeshBuilder>();
  constructor(
    readonly chunkSize: number,
    readonly origin: number,
    /** Material keys merged into another key's builders (fewer draw calls). */
    readonly aliases: Record<string, string> = {},
  ) {}

  chunkOf(x: number, z: number): string {
    const cx = Math.floor((x - this.origin) / this.chunkSize);
    const cz = Math.floor((z - this.origin) / this.chunkSize);
    return `${cx},${cz}`;
  }

  get(material: string, x: number, z: number): MeshBuilder {
    const key = `${this.aliases[material] ?? material}|${this.chunkOf(x, z)}`;
    let b = this.map.get(key);
    if (!b) {
      b = new MeshBuilder();
      this.map.set(key, b);
    }
    return b;
  }

  /** Single global builder for a material (no chunking). */
  global(material: string): MeshBuilder {
    const key = `${this.aliases[material] ?? material}|global`;
    let b = this.map.get(key);
    if (!b) {
      b = new MeshBuilder();
      this.map.set(key, b);
    }
    return b;
  }

  /** Remove and return a material's builders by chunk (for merging them into another key). */
  take(material: string): Map<string, MeshBuilder> {
    const out = new Map<string, MeshBuilder>();
    for (const [k, b] of [...this.map.entries()]) {
      const [m, c] = k.split('|') as [string, string];
      if (m !== material) continue;
      out.set(c, b);
      this.map.delete(k);
    }
    return out;
  }

  /** Put a builder in as `material`'s builder of `chunk` (replacing any). */
  put(material: string, chunk: string, b: MeshBuilder): void {
    this.map.set(`${this.aliases[material] ?? material}|${chunk}`, b);
  }

  /** The builder of `material` in chunk `chunk` (a key from chunkOf, or 'global'). */
  at(material: string, chunk: string): MeshBuilder {
    const key = `${this.aliases[material] ?? material}|${chunk}`;
    let b = this.map.get(key);
    if (!b) {
      b = new MeshBuilder();
      this.map.set(key, b);
    }
    return b;
  }

  entries(): [material: string, chunk: string, builder: MeshBuilder][] {
    return [...this.map.entries()].map(([k, b]) => {
      const [m, c] = k.split('|') as [string, string];
      return [m, c, b];
    });
  }
}

function triNormal(p0: ArrayLike<number>, p1: ArrayLike<number>, p2: ArrayLike<number>): [number, number, number] {
  const ax = p1[0]! - p0[0]!;
  const ay = p1[1]! - p0[1]!;
  const az = p1[2]! - p0[2]!;
  const bx = p2[0]! - p0[0]!;
  const by = p2[1]! - p0[1]!;
  const bz = p2[2]! - p0[2]!;
  return [ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx];
}

export function hexRGB(hex: number, mul = 1): RGB {
  return [(((hex >> 16) & 255) / 255) * mul, (((hex >> 8) & 255) / 255) * mul, ((hex & 255) / 255) * mul];
}

/** Deterministic hash -> [0,1). */
export function hash01(a: number, b = 0, c = 0): number {
  let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}
